import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { PaymentRecord } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ColumnDef } from '@tanstack/react-table';

const providerLabel = (p: PaymentRecord): string =>
  p.provider === 'manual' ? 'Invoice / manual'
    : p.provider === 'soxara' ? 'Soxara checkout'
      : p.provider || p.method;

export const PaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      const data = await api.getPayments();
      setPayments(data);
    } catch (err) {
      console.error('Failed to load payments', err);
    }
  };

  const totalCollected = payments
    .filter((p) => p.status === 'succeeded')
    .reduce((acc, p) => acc + p.amountUsd, 0);

  const columns = useMemo<ColumnDef<PaymentRecord>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Transaction Date',
        cell: (info) => (
          <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            {new Date(info.getValue() as string).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'customerName',
        header: 'Customer Account',
        cell: (info) => (
          <div>
            <strong style={{ color: 'var(--text-main)' }}>{info.getValue() as string}</strong>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
              Plan: {info.row.original.planName}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'amountUsd',
        header: 'Amount',
        cell: (info) => (
          <span style={{ fontWeight: 700, color: 'var(--text-main)', fontSize: '14px' }}>
            ${(info.getValue() as number).toFixed(2)} USD
          </span>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        accessorFn: (p) => providerLabel(p),
        cell: (info) => (
          <span style={{ fontSize: '12px', fontWeight: 500 }}>{providerLabel(info.row.original)}</span>
        ),
      },
      {
        accessorKey: 'reference',
        header: 'Reference / notes',
        cell: (info) => {
          const v = info.getValue() as string;
          return v ? <code style={{ fontSize: 11 }}>{v}</code> : <span style={{ color: 'var(--text-dim)' }}>—</span>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue() as string} />,
      },
    ],
    []
  );

  return (
    <div>
      {/* Top Banner with Platform Ledger Aggregates */}
      <div
        style={{
          background: 'var(--bg-surface-card)',
          border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 24px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>
            Platform Payment Ledger
          </h2>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Manually-recorded invoice payments and Soxara hosted checkouts.
          </p>
        </div>

        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-dim)', textTransform: 'uppercase' }}>
            Total Settled Volume
          </div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--success-text)' }}>
            ${totalCollected.toLocaleString('en-US', { minimumFractionDigits: 2 })} USD
          </div>
        </div>
      </div>

      <DataTable
        storageKey="payments"
        data={payments}
        columns={columns}
        searchPlaceholder="Search customer, reference, payment..."
        exportFilename="mwm_payments_ledger.csv"
      />
    </div>
  );
};
